/**
 * Shared formatting + FIFO helpers for the ink trolley screens.
 *
 * Pulled out of InkTrolleyView so the kit page, the run list and the reconcile
 * dialog all round the same way. Jack, 2026-09-09: "no further decimals than 1"
 * for kilograms; money stays at 2 because rounding a supplier's 16.59 to 16.6
 * would silently change a price on the next save.
 */
import { InkUnitRow } from '../productionApi';

/** At most 1dp, trailing ".0" dropped so 36 reads as "36", not "36.0". */
export function fmt1(v: number): string {
  return (Math.round(v * 10) / 10).toLocaleString(undefined, { maximumFractionDigits: 1 });
}

export function fmtKg(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${fmt1(v)} kg`;
}

export function fmtMoney(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return `$${(Math.round(v * 100) / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return 'no due date';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'no due date';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
}

/** Backend errors are {success:false, error:"..."} — those messages are genuinely useful. */
export function errMsg(err: unknown): string {
  const anyErr = err as { response?: { data?: { error?: string } }; message?: string };
  return anyErr?.response?.data?.error ?? anyErr?.message ?? 'Unknown error';
}

/**
 * The batch a picker should take next for an ink, and how much of it is there.
 *
 * FIFO is by BATCH, not by tub: any tub within the oldest batch is equally
 * correct. `batch_no` is a monotonic receipt sequence, so the lowest one at a
 * location is the oldest — the same rule the backend's take_sealed_tubs()
 * applies with `ORDER BY b.received_at`.
 */
export interface FifoHint {
  /** Oldest batch still sealed at the location. */
  batchNo: string;
  /** Sealed tubs of that batch, before anything is scanned. */
  sealedTubs: number;
  /** How many distinct batches of this ink are sealed there — >1 means FIFO can be got wrong. */
  batchCount: number;
  /** Every tub's nominal weight in the oldest batch; the tub size for this ink. */
  nominalKg: number | null;
}

export function fifoHints(units: InkUnitRow[]): Map<string, FifoHint> {
  const byInk = new Map<string, InkUnitRow[]>();
  units.forEach((u) => {
    const a = byInk.get(u.ink_key) ?? [];
    a.push(u);
    byInk.set(u.ink_key, a);
  });
  const out = new Map<string, FifoHint>();
  byInk.forEach((list, inkKey) => {
    const batches = new Set(list.map((u) => u.batch_no));
    const oldest = Array.from(batches).sort()[0];
    const inOldest = list.filter((u) => u.batch_no === oldest);
    out.set(inkKey, {
      batchNo: oldest,
      sealedTubs: inOldest.length,
      batchCount: batches.size,
      nominalKg: inOldest.length > 0 ? Number(inOldest[0].nominal_kg) : null,
    });
  });
  return out;
}

/**
 * Whole tubs needed to cover a kg demand.
 *
 * A batch cannot be split at a non-tub boundary (migration 116/117), so a kg
 * ask always rounds UP — 36.7 kg of a 2 kg ink is 19 tubs, not 18.35. Mirrors
 * take_sealed_tubs(cover_kg=...) exactly; if these ever disagree the screen is
 * lying about what the backend will do.
 */
export function tubsToCover(demandKg: number, tubKg: number | null | undefined): number | null {
  if (!tubKg || !Number.isFinite(tubKg) || tubKg <= 0) return null;
  if (!Number.isFinite(demandKg) || demandKg <= 0) return 0;
  return Math.ceil(demandKg / tubKg);
}
